import React from 'react';
import { Check } from 'lucide-react';
import VoiceMessagePlayer from './VoiceMessagePlayer';

export function normalizeDataUri(dataUri, mediaType) {
  if (!dataUri || !dataUri.startsWith('data:')) return dataUri;
  if (dataUri.match(/^data:[a-z]+\/[a-z0-9+-]+;base64,/)) return dataUri;
  if (mediaType === 'audio' && dataUri.startsWith('data:audio;base64,')) {
    try {
      const base64Data = dataUri.substring(dataUri.indexOf(',') + 1);
      const binaryString = atob(base64Data.substring(0, 100));
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) bytes[i] = binaryString.charCodeAt(i);
      if (bytes[0] === 0x4F && bytes[1] === 0x67 && bytes[2] === 0x67 && bytes[3] === 0x53) {
        return dataUri.replace('data:audio;base64,', 'data:audio/ogg;base64,');
      }
      return dataUri.replace('data:audio;base64,', 'data:audio/ogg;base64,');
    } catch (e) {
      return dataUri.replace('data:audio;base64,', 'data:audio/ogg;base64,');
    }
  }
  if (mediaType === 'image' && dataUri.startsWith('data:image;base64,')) {
    return dataUri.replace('data:image;base64,', 'data:image/png;base64,');
  }
  return dataUri;
}

function renderMessageContent(msg, { isOutgoing, isDarkMode, onImageClick }) {
  let content = msg.message || msg.content || '';

  if (msg.topic === 'dietitian_image' && content.trim().startsWith('{')) {
    try {
      const parsed = JSON.parse(content);
      const imageField = parsed.image;
      if (typeof imageField === 'string' && imageField.length > 0) {
        const imageSrc = imageField.startsWith('data:image')
          ? normalizeDataUri(imageField, 'image')
          : imageField;
        const caption =
          parsed.text != null && typeof parsed.text !== 'object'
            ? String(parsed.text).trim()
            : '';
        const captionClass = isOutgoing
          ? 'text-white/90'
          : isDarkMode
            ? 'text-ink-muted-dark'
            : 'text-ink-muted';
        return (
          <div className="space-y-1">
            <img
              src={imageSrc}
              alt={caption || 'Image'}
              className="rounded-lg max-h-64 object-cover cursor-pointer hover:opacity-90 border border-line dark:border-line-dark"
              onClick={() => onImageClick(imageSrc)}
            />
            {caption ? (
              <div className={`text-[13px] whitespace-pre-wrap break-words ${captionClass}`}>{caption}</div>
            ) : null}
          </div>
        );
      }
    } catch (e) {
      /* fall through */
    }
  }

  if (msg.role === 'assistant' && content.trim().startsWith('{')) {
    try {
      const parsed = JSON.parse(content);
      content = parsed.response_text || '';
    } catch (e) { /* keep */ }
  } else if (msg.topic === 'system_reply' && content.trim().startsWith('{')) {
    try {
      const parsed = JSON.parse(content);
      if (parsed.agent_response) {
        const ar = JSON.parse(parsed.agent_response);
        if (ar.response_text) content = ar.response_text;
      }
    } catch (e) { /* keep */ }
  } else if (content.trim().startsWith('{')) {
    try {
      const parsed = JSON.parse(content);
      if (parsed.response_text) content = parsed.response_text;
    } catch (e) { /* keep */ }
  }
  content = content.replace(/\[.*?\]/g, '').replace(/button:\s*[^\n]+/gi, '').trim();

  const hasBase64Image = content.startsWith('data:image');
  const hasBase64Audio = content.startsWith('data:audio');
  const normalizedImg = hasBase64Image ? normalizeDataUri(content, 'image') : null;
  const normalizedAudio = hasBase64Audio ? normalizeDataUri(content, 'audio') : null;
  const isBase64 = hasBase64Image || hasBase64Audio;
  const text = !isBase64 ? content : '';

  const media = [];
  if (hasBase64Image && normalizedImg) {
    media.push(
      <img
        key="b64i"
        src={normalizedImg}
        alt="Attached"
        className="rounded-lg max-h-64 object-cover cursor-pointer hover:opacity-90 border border-line dark:border-line-dark mb-2"
        onClick={() => onImageClick(normalizedImg)}
      />
    );
  }
  if (hasBase64Audio && normalizedAudio) {
    media.push(
      <div key="b64a" className={text ? 'mb-2' : ''}>
        <VoiceMessagePlayer src={normalizedAudio} isOutgoing={!!isOutgoing} isDarkMode={isDarkMode} />
      </div>
    );
  }
  if (msg.attachments && Array.isArray(msg.attachments)) {
    msg.attachments.forEach((att, i) => {
      const url = att.url || att;
      const type = att.type || att.mime_type || '';
      if (type.startsWith('image/') || (typeof url === 'string' && url.match(/\.(jpg|png|webp|gif)/i))) {
        media.push(
          <img
            key={i}
            src={url}
            alt="Attachment"
            className="rounded-lg max-h-64 object-cover cursor-pointer hover:opacity-90 border border-line dark:border-line-dark mt-2"
            onClick={() => onImageClick(url)}
          />
        );
      } else if (type.startsWith('audio/') || (typeof url === 'string' && url.match(/\.(mp3|wav|ogg)/i))) {
        media.push(
          <div key={i} className={i > 0 || text || hasBase64Image || hasBase64Audio ? 'mt-2' : ''}>
            <VoiceMessagePlayer src={url} isOutgoing={!!isOutgoing} isDarkMode={isDarkMode} />
          </div>
        );
      } else if (type.startsWith('video/')) {
        media.push(<video key={i} src={url} controls className="rounded-lg max-h-64 mt-2" />);
      } else if (url) {
        media.push(
          <a
            key={i}
            href={url}
            download
            className="block mt-2 text-[13px] text-progress underline underline-offset-2"
          >
            Download file
          </a>
        );
      }
    });
  }

  return (
    <div className="space-y-1">
      {media}
      {text && <div className="whitespace-pre-wrap break-words">{text}</div>}
    </div>
  );
}

export default function ChatBubble({ event, isDarkMode, onImageClick, timeLabel }) {
  const msg = event.payload;
  const isUser = msg.sender === 'user';

  return (
    <div className={`flex w-full ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`
          relative max-w-[85%] sm:max-w-[70%] xl:max-w-[60%] px-4 py-3 text-[13px] leading-relaxed
          rounded-lg
          ${isUser
            ? 'bg-forest-ondark text-white'
            : `bg-surface dark:bg-surface-dark text-ink dark:text-ink-dark border border-line dark:border-line-dark`}
        `}
      >
        {renderMessageContent(msg, { isOutgoing: isUser, isDarkMode, onImageClick })}
        <div
          className={`
            text-[11px] mt-1.5 flex items-center justify-end gap-1 font-medium
            ${isUser ? 'text-white/70' : 'text-ink-muted dark:text-ink-muted-dark'}
          `}
        >
          {timeLabel}
          {isUser && <Check size={12} strokeWidth={3} aria-hidden="true" />}
        </div>
      </div>
    </div>
  );
}
